import 'configuration/ag-grid/ag-grid.init';
import type { ColTypeDef, GetRowIdParams, GridApi, GridReadyEvent } from 'ag-grid-community';
import { MasterDetailModule } from 'ag-grid-enterprise';
import {
  IPriceImprovementBlotterRequest,
  IPriceImprovementBlotterRequestTopRequest,
  priceImprovementBlotterApiConnector,
} from 'apiConnector/blotters/priceImprovementBlotter';
import { ServerSideBlotterGrid } from 'components/shared/Blotter/Grid/blotter-serverside';
import type { StatusToColorMapper } from 'components/shared/Blotter/Grid/CellRenderers';
import { useDefaultColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnDefinition';
import { useDefaultGroupColumnDefinition } from 'components/shared/Blotter/Grid/Hooks/useDefaultColumnGroupDefinition';
import {
  useDefaultFilterTranslators,
  useDefaultSortTranslator,
} from 'components/shared/Blotter/Grid/Hooks/useDefaultTranslators';
import { useGridAutoRefresh } from 'components/shared/Blotter/Grid/Hooks/useGridAutoRefresh';
import { createMasterDetailTechnicalColumn } from 'components/shared/Blotter/Grid/Hooks/useMasterDetailTechnicalColumn';
import { useRelativeTimeColumnType } from 'components/shared/Blotter/Grid/Hooks/useRelativeTimeColumnType';
import { useTranslateFilters } from 'components/shared/Blotter/Grid/Hooks/useRequestTranslator';
import { useServerSideBlotterDataSource } from 'components/shared/Blotter/Grid/Hooks/useServerSideDatasource';
import { useServerSideDefaultColumnTypes } from 'components/shared/Blotter/Grid/Hooks/useServerSideDefaultColumnTypes';
import { useServerSideStatusColumnType } from 'components/shared/Blotter/Grid/Hooks/useStatusColumnType';
import { useTimespanColumnType } from 'components/shared/Blotter/Grid/Hooks/useTimespanColumnType';
import { getHeaderForBlotterIntlKey } from 'components/shared/Blotter/utils';
import { useCallback, useMemo, useState } from 'react';
import type { IPriceImprovementBlotterItem } from 'state/blotters/priceImprovement';
import { httpClient } from 'utils/http/httpClient';

import { useContextMenuItems } from '../../../shared/Blotter/Grid/Hooks/useContextMenuItems';
import { useColoredNumberType } from './hooks';
import { SensitivityDetailCellRenderer } from './masterDetail/masterDetail';
import type { PriceImprovementBlotterServerSideGridProps } from './PriceImprovementBlotterServerSideGrid.connect';

const getHeaderIntlKey = getHeaderForBlotterIntlKey('priceImprovementBlotter');
const getPriceImprovementStatusColor: StatusToColorMapper<string> = status => {
  switch (status) {
    case 'Pending':
      return 'warning';
    case 'Acknowledged':
      return 'info';
    case 'Validated':
      return 'success';
    case 'Rejected':
      return 'danger';
    case 'Cancelled':
    case 'Expired':
      return 'secondary';

    default:
      return undefined;
  }
};
const selectColWidth = 80;
const defaultColumnWidth = { width: 200 };
const masterDetail = createMasterDetailTechnicalColumn<IPriceImprovementBlotterItem>(
  { checkboxSelection: true },
  selectColWidth,
);
const agModules = [MasterDetailModule];
export function RawPriceImprovementBlotterServerSideGrid({
  blotterRequest,
  columnState,
  columnGroupState,
  pageSizeState,
  columnDefinitions,
  onBlotterColumnStateChanged,
  onSelectedItemsChanged,
  onItemsReceived,
  onItemsRequested,
  onRequestFailed,
}: PriceImprovementBlotterServerSideGridProps) {
  const [api, setApi] = useState<GridApi>();
  const onAgGridReady = useCallback((event: GridReadyEvent) => {
    setApi(event.api);
  }, []);

  const defaultColDef = useDefaultColumnDefinition<IPriceImprovementBlotterItem>(getHeaderIntlKey, defaultColumnWidth);
  const defaultGroupColumnDef = useDefaultGroupColumnDefinition(getHeaderIntlKey);
  const coloredNumberColumn = useColoredNumberType<IPriceImprovementBlotterItem>();
  const statusColumn = useServerSideStatusColumnType<IPriceImprovementBlotterItem>(getPriceImprovementStatusColor);
  const timespanColumn = useTimespanColumnType<IPriceImprovementBlotterItem>();
  const relativetimeColumn = useRelativeTimeColumnType<IPriceImprovementBlotterItem>();

  const customColumns = useMemo<Record<string, ColTypeDef<IPriceImprovementBlotterItem>>>(
    () => ({
      status: statusColumn,
      timespan: timespanColumn,
      coloredNumber: coloredNumberColumn,
      relativeTime: relativetimeColumn,
      stringWithRowSelector: masterDetail,
    }),
    [coloredNumberColumn, relativetimeColumn, statusColumn, timespanColumn],
  );
  const columnTypes = useServerSideDefaultColumnTypes<IPriceImprovementBlotterItem>(customColumns);
  const getContextMenuItems = useContextMenuItems('Blotter My price improvements', api);

  const translateSort = useDefaultSortTranslator();
  const defaultFilterTranslators = useDefaultFilterTranslators();
  const translateFilters = useTranslateFilters(defaultFilterTranslators);

  const serverSideDatasource = useServerSideBlotterDataSource<
    IPriceImprovementBlotterRequest,
    IPriceImprovementBlotterRequestTopRequest,
    IPriceImprovementBlotterItem,
    Error
  >({
    blotterRequest,
    searchItems: priceImprovementBlotterApiConnector(httpClient).search,
    translateRequest: (gridRequest, topRequest) => {
      const filters = translateFilters(gridRequest);
      const sort = translateSort(gridRequest);
      return { ...topRequest, filters, sort };
    },
    onItemsRequested,
    onItemsReceived,
    onRequestFailed,
    isNativePaginationEnabled: false,
  });

  const isDateFilterCoherent =
    blotterRequest.endDate !== undefined && blotterRequest.startDate < blotterRequest.endDate;
  useGridAutoRefresh(isDateFilterCoherent ? api : null);

  return (
    <ServerSideBlotterGrid
      name="priceImprovementBlotter"
      logAnalyticName="Blotter My price improvements"
      e2e="price-improvement-blotter"
      onGridReady={onAgGridReady}
      columnDefinitions={columnDefinitions}
      defaultColDef={defaultColDef}
      defaultColGroupDef={defaultGroupColumnDef}
      getRowId={getRowId}
      columnTypes={columnTypes}
      masterDetail
      detailRowAutoHeight
      detailCellRenderer={SensitivityDetailCellRenderer}
      getContextMenuItems={getContextMenuItems}
      columnState={columnState}
      columnGroupState={columnGroupState}
      pageSizeState={pageSizeState}
      onSelectedItemsChanged={onSelectedItemsChanged}
      onBlotterColumnStateChanged={onBlotterColumnStateChanged}
      modules={agModules}
      serverSideDatasource={serverSideDatasource}
      rowSelection={{ mode: 'multiRow', checkboxes: false, headerCheckbox: false, enableClickSelection: true }}
    />
  );
}

function getRowId(p: GetRowIdParams) {
  return (p.data as IPriceImprovementBlotterItem).id;
}
